<template>
  <div class="station-invoice-container">
    <!-- 页面头部 -->
    <!-- <div class="page-header">
      <div class="station-info" v-if="stationInfo">
        <span class="station-label">场站：</span>
        <span class="station-name">{{ stationInfo.name }}</span>
      </div>
    </div> -->

    <!-- 步骤1: 车牌号查询和订单选择 -->
    <div v-if="currentStep === 1" class="step1-container">
      <!-- 搜索提示 -->
      <div class="search-tip">
        <van-notice-bar
          left-icon="info-o"
          :text="searchTipText"
          background="#ecf9ff"
          color="#1989fa"
        />
      </div>

      <!-- 车牌号查询 -->
      <div class="search-section">
        <van-cell-group inset>
          <van-field
            v-if="stationInfo"
            label="车场名称"
            :value="stationInfo.name"
            readonly
            class="station-name-field"
          />
          <van-field
            v-model="plateNumber"
            label="车牌号"
            placeholder="请输入车牌号"
            clearable
            center
            :rules="plateNumberRules"
            class="plate-number-field"
          >
            <template #button>
              <van-button
                size="small"
                type="primary"
                :loading="searchLoading"
                @click="searchOrders"
                class="search-button"
              >
                查询
              </van-button>
            </template>
          </van-field>
        </van-cell-group>
      </div>

      <!-- 订单记录列表 -->
      <div class="orders-section" v-if="orderList.length > 0">
        <div class="section-title">
          <span>订单记录（最近30天）</span>
          <span class="order-count">共{{ orderList.length }}条</span>
        </div>

        <div class="scrollable-orders">
          <van-radio-group v-model="selectedOrderId">
            <van-cell-group inset>
              <van-cell
                v-for="order in orderList"
                :key="order.id"
                :title="formatOrderTitle(order)"
                :label="formatOrderLabel(order)"
                clickable
                @click="selectOrder(order.id)"
              >
                <template #right-icon>
                  <van-radio
                    :name="order.id"
                    @click.stop
                  />
                </template>
              </van-cell>
            </van-cell-group>
          </van-radio-group>
        </div>

        <!-- 选中订单汇总 -->
        <div class="order-summary">
          <div class="summary-info">
            <span>已选择{{ selectedOrderCount }}条订单</span>
            <span class="total-amount">合计：¥{{ totalAmount }}</span>
          </div>
        </div>

        <!-- 下一步按钮 -->
        <div class="fixed-step-buttons">
          <van-button
            round
            block
            type="primary"
            @click="goToStep2"
            class="next-step-button"
          >
            下一步
          </van-button>
        </div>
      </div>

      <!-- 空状态 -->
      <van-empty
        v-if="hasSearched && orderList.length === 0 && !searchLoading"
        description="未找到相关订单记录"
        image="search"
      />
    </div>

    <!-- 步骤2: 发票信息表单 -->
    <div v-if="currentStep === 2">
      <!-- 选中订单汇总 (只读) -->
      <div class="selected-orders-summary">
        <div class="section-title">已选择的订单</div>
        <div class="scrollable-orders">
          <van-cell-group inset>
            <van-cell
              v-if="selectedOrderDetail"
              :key="selectedOrderDetail.id"
              :title="formatOrderTitle(selectedOrderDetail)"
              :label="formatOrderLabel(selectedOrderDetail)"
            >
              <template #right-icon>
                <van-button
                  size="small"
                  type="danger"
                  plain
                  @click="removeOrder(selectedOrderDetail.id)"
                >
                  删除
                </van-button>
              </template>
            </van-cell>
          </van-cell-group>
        </div>
        <div class="total-summary">
          <span>共{{ selectedOrderCount }}条订单</span>
          <span class="total-amount">合计：¥{{ totalAmount }}</span>
        </div>
      </div>

      <!-- 发票信息表单 -->
    <div class="invoice-form-section" v-if="selectedOrderId">
      <div class="section-title">发票信息</div>

      <van-form @submit="submitInvoice" ref="invoiceForm">
        <van-cell-group inset>
          <!-- 发票类型 -->
          <van-field
            readonly
            clickable
            name="invoiceType"
            :value="invoiceTypeText"
            label="发票类型"
            placeholder="点击选择发票类型"
            is-link
            @click.native="showTypePicker = true"
          />

          <!-- 发票抬头信息 -->
          <van-field
            v-model="invoiceForm.companyName"
            name="companyName"
            :label="invoiceForm.invoiceType === 2 ? '公司名称' : (invoiceForm.invoiceType === 1 ? '个人姓名' : '名称')"
            :placeholder="invoiceForm.invoiceType === 2 ? '请输入公司名称' : (invoiceForm.invoiceType === 1 ? '请输入个人姓名' : '请输入名称')"
            :rules="[{ required: true, message: invoiceForm.invoiceType === 2 ? '请输入公司名称' : (invoiceForm.invoiceType === 1 ? '请输入个人姓名' : '请输入名称') }]"
          />

          <van-field
            v-if="invoiceForm.invoiceType !== 3"
            v-model="invoiceForm.taxId"
            name="taxId"
            :label="invoiceForm.invoiceType === 2 ? '税号' : '身份证号'"
            :placeholder="invoiceForm.invoiceType === 2 ? '请输入税号' : '请输入身份证号'"
            :rules="[
              { required: true, message: invoiceForm.invoiceType === 2 ? '请输入税号' : '请输入身份证号' },
              { pattern: invoiceForm.invoiceType === 2 ? /^(([0-9A-Za-z]{15})|([0-9A-Za-z]{18})|([0-9A-Za-z]{20}))$/ : /^[1-9]\d{5}(?:18|19|20)\d{2}(?:0[1-9]|10|11|12)(?:0[1-9]|[1-2]\d|30|31)\d{3}[\dXx]$/, message: invoiceForm.invoiceType === 2 ? '税号格式不正确' : '身份证号格式不正确' }
            ]"
          />

          <van-field
            v-if="invoiceForm.invoiceType === 2"
            v-model="invoiceForm.address"
            name="address"
            label="地址"
            placeholder="请输入地址"
          />

          <!-- 联系信息 -->
          <van-field
            v-model="invoiceForm.email"
            name="email"
            label="邮箱"
            placeholder="请输入邮箱地址"
            :rules="emailRules"
          />

          <van-field
            v-model="invoiceForm.mobile"
            name="mobile"
            label="手机号"
            placeholder="请输入手机号"
            :rules="mobileRules"
          />

          <!-- 通行凭证上传 -->
          <van-field name="paymentProof" label="支付凭证">
            <template #input>
              <van-uploader
                v-model="invoiceForm.paymentProof"
                :max-count="1"
                :after-read="afterRead"
                :before-delete="beforeDelete"
              >
                <van-button icon="photograph" type="primary" size="small">
                  上传凭证
                </van-button>
              </van-uploader>
            </template>
          </van-field>
        </van-cell-group>

        <!-- 步骤按钮 -->
        <div class="fixed-step-buttons step-2-buttons">
          <van-button
            round
            block
            plain
            type="primary"
            @click="goToStep1"
            class="prev-button"
          >
            上一步
          </van-button>
          <van-button
            round
            block
            type="primary"
            native-type="submit"
            :loading="submitLoading"
            class="submit-button"
          >
            提交申请
          </van-button>
        </div>
      </van-form>
    </div>
    </div>

    <!-- 发票类型选择器 -->
    <van-popup v-model="showTypePicker" position="bottom" get-container="body">
      <van-picker
        show-toolbar
        :columns="invoiceTypeColumns"
        @confirm="onConfirmType"
        @cancel="showTypePicker = false"
      />
    </van-popup>
  </div>
</template>

<script>
import { Toast, Dialog } from 'vant'
import { getInvoicePkInfo, getPkValidOrderPage, applyInvoice } from '@/api/modules/parking'

export default {
  name: 'StationInvoiceGenerate',
  data() {
    return {
      // 当前步骤 (1: 订单选择, 2: 发票信息)
      currentStep: 1,

      // 场站信息
      stationInfo: null,

      // 车牌号查询
      plateNumber: '',
      searchLoading: false,
      hasSearched: false,

      // 订单记录数据
      orderList: [],
      selectedOrderId: null,

      // 发票表单
      invoiceForm: {
        invoiceType: 1, // 1: 个人, 2: 企业, 3: 非企业
        companyName: '',
        taxId: '',
        address: '',
        email: '',
        mobile: '',
        paymentProof: []
      },
      showTypePicker: false,
      invoiceTypeColumns: [
        { text: '个人', value: 1 },
        { text: '企业', value: 2 },
        { text: '非企业', value: 3 }
      ],
      submitLoading: false,

      // 表单验证规则
      plateNumberRules: [
        { required: true, message: '请输入车牌号' }
      ],
      emailRules: [
        { required: true, message: '请输入邮箱地址' },
        { pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: '请输入正确的邮箱格式' }
      ],
      mobileRules: [
        { required: true, message: '请输入手机号' },
        { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号格式' }
      ]
    }
  },

  computed: {
    searchTipText() {
      if (this.stationInfo) {
        return `场站：${this.stationInfo.name} - 请输入车牌号查询订单记录`
      }
      return '请输入车牌号查询订单记录'
    },

    totalAmount() {
      if (!this.selectedOrderId) {
        return '0.00'
      }
      const order = this.orderList.find(o => o.id === this.selectedOrderId)
      return (order ? order.amount : 0).toFixed(2)
    },

    selectedOrderDetail() {
      if (!this.selectedOrderId) return null
      return this.orderList.find(o => o.id === this.selectedOrderId) || null
    },

    selectedOrderCount() {
      return this.selectedOrderId ? 1 : 0
    },

    invoiceTypeText() {
      const type = this.invoiceTypeColumns.find(item => item.value === this.invoiceForm.invoiceType)
      return type ? type.text : ''
    }
  },

  created() {
    this.initPage()
  },

  methods: {
    // 初始化页面
    initPage() {
      const stationId = this.$route.query.parkId
      console.log(stationId, 'stationId')
      if (stationId) {
        this.loadStationInfo(stationId)
      }
    },

    // 加载场站信息
    async loadStationInfo(stationId) {
      try {
        const res = await getInvoicePkInfo({ parkId: stationId })

        if (res && typeof res === 'object') {
          // 接口返回格式为 { "场站名称": parkId }
          const parkNames = Object.keys(res)
          const parkName = parkNames.length > 0 ? parkNames[0] : '未知场站'

          this.stationInfo = {
            id: stationId,
            name: parkName,
            address: '未知地址'
          }
        }
      } catch (error) {
        console.error('加载场站信息失败:', error)
      }
    },

    // 确认发票类型
    onConfirmType(option) {
      this.invoiceForm.invoiceType = option.value
      this.showTypePicker = false
    },

    // 搜索订单记录
    async searchOrders() {
      if (!this.plateNumber.trim()) {
        Toast('请输入车牌号')
        return
      }

      const parkId = this.$route.query.parkId
      if (!parkId) {
        Toast('未获取到场站ID')
        return
      }

      this.searchLoading = true
      this.hasSearched = true

      try {
        const res = await getPkValidOrderPage({
          parkId: parkId,
          licensePlateNumber: this.plateNumber,
          size: 10,
          current: 1
        })

        if (res && res.records) {
          this.orderList = res.records.map(item => ({
            id: item.id,
            plateNumber: item.licensePlateNumber,
            date: item.createTime,
            amount: parseFloat(item.payAmount || 0),
            status: item.orderStatus === 1 ? '已完成' : '待支付',
            stationName: item.parkName || (this.stationInfo ? this.stationInfo.name : '未知场站'),
            duration: parseFloat(item.parkTime || 0),
            vehicleType: item.carType || '车辆'
          }))
        } else {
          this.orderList = []
        }

        if (this.orderList.length === 0) {
          Toast('未找到相关订单记录')
        } else {
          Toast(`找到${this.orderList.length}条订单记录`)
        }
      } catch (error) {
        Toast('查询失败，请重试')
        console.error('Search orders error:', error)
      } finally {
        this.searchLoading = false
      }
    },

    // 生成模拟订单记录数据
    generateMockOrders(plateNumber) {
      const orders = []
      const orderCount = 30 // 30条订单
      const now = new Date()

      const stations = ['昆明西场站', '昆明东场站', '昆明南场站', '昆明北场站', '安宁场站', '嵩明场站']
      const vehicleTypes = ['小型车', '中型车', '大型车']

      for (let i = 0; i < orderCount; i++) {
        const daysAgo = Math.floor(Math.random() * 30) // 30天内
        const orderDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000)
        const amount = (Math.random() * 50 + 5).toFixed(2) // 5-55元

        orders.push({
          id: `ORDER_${Date.now()}_${i}`,
          plateNumber: plateNumber,
          date: orderDate,
          amount: parseFloat(amount),
          status: '已完成',
          stationName: stations[i % stations.length],
          duration: (Math.floor(Math.random() * 180) + 30) / 60, // 0.5-3.5小时
          vehicleType: vehicleTypes[Math.floor(Math.random() * vehicleTypes.length)]
        })
      }

      return orders.sort((a, b) => b.date - a.date) // 按日期倒序
    },

    // 格式化订单记录标题
    formatOrderTitle(order) {
      return order.stationName
    },

    // 格式化订单记录标签
    formatOrderLabel(order) {
      const dateStr = this.$moment(order.date).format('MM-DD HH:mm')
      return `${dateStr} | ¥${order.amount} | ${order.duration.toFixed(1)}小时`
    },

    // 切换订单选择
    selectOrder(orderId) {
      this.selectedOrderId = orderId
    },

    // 移除已选择的订单
    removeOrder(orderId) {
      if (this.selectedOrderId === orderId) {
        this.selectedOrderId = null

        // 如果没有选中的订单了，返回第一步
        this.currentStep = 1
      }
    },

    // 步骤导航
    goToStep2() {
      if (!this.selectedOrderId) {
        Toast('请选择要开票的订单记录')
        return
      }
      this.currentStep = 2
    },

    goToStep1() {
      this.currentStep = 1
    },

    // 文件上传后处理
    afterRead(file) {
      console.log('File uploaded:', file)
      // 这里可以添加文件上传到服务器的逻辑
    },

    // 删除文件前确认
    beforeDelete() {
      return Dialog.confirm({
        title: '确认删除',
        message: '确定要删除这张图片吗？'
      })
    },

    // 提交发票申请
    async submitInvoice(values) {
      if (!this.selectedOrderId) {
        Toast('请选择要开票的订单记录')
        return
      }

      if (this.invoiceForm.paymentProof.length === 0) {
        Toast('请上传支付凭证')
        return
      }

      const parkId = this.$route.query.parkId
      if (!parkId) {
        Toast('未获取到场站ID')
        return
      }

      this.submitLoading = true

      try {
        // 构造请求数据
        const invoiceData = {
          parkId: parseInt(parkId),
          applyType: 1, // 1:用户申请
          orderNos: this.selectedOrderId ? [this.selectedOrderId] : [], // 仅支持单个订单
          licensePlateNumber: this.plateNumber,
          buyerName: this.invoiceForm.companyName,
          buyerTaxCode: this.invoiceForm.taxId || '',
          type: this.invoiceForm.invoiceType, // 1:个人, 2:企业, 3:非企业
          buyerAddress: this.invoiceForm.address || '',
          buyerTelephone: this.invoiceForm.mobile || '',
          buyerBank: '',
          buyerAccount: '',
          email: this.invoiceForm.email,
          payImages: this.invoiceForm.paymentProof.map(item => item.content).filter(Boolean),
          payImageUrls: '',
          remark: ''
        }

        console.log('Invoice submit data:', invoiceData)

        await applyInvoice(invoiceData)

        Toast.success('发票申请提交成功！')

        setTimeout(() => {
          this.$router.push('/station-invoice/success')
        }, 1500)
      } catch (error) {
        Toast.fail(error.message || '提交失败，请重试')
        console.error('Submit invoice error:', error)
      } finally {
        this.submitLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.station-invoice-container {
  min-height: 100vh;
  background: url('~@/assets/images/bg-imgs/station-invoice-bg.png');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  padding-bottom: calc(80px + env(safe-area-inset-bottom));

  // 隐藏主容器滚动条
  overflow-x: hidden;
  scrollbar-width: none; // Firefox
  -ms-overflow-style: none; // IE/Edge

  &::-webkit-scrollbar {
    display: none; // WebKit browsers
  }
}

// 步骤1容器使用弹性布局
.step1-container {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 88px - env(safe-area-inset-bottom));
}

.page-header {
  color: $white;
  padding: 20px 16px;
  text-align: center;
  padding-bottom: 0;

  &-title {
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 8px;
  }

  &-text {
    font-size: 14px;
    opacity: 0.9;
    margin-bottom: 12px;
  }

  .station-info {
    font-size: 12px;
    opacity: 0.8;

    .station-label {
      margin-right: 4px;
    }
  }
}

.search-tip {
  margin: 12px 16px 0;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

// 车牌号输入框垂直居中样式
.plate-number-field {
  :deep(.van-field__label) {
    display: flex;
    align-items: center;
    height: 100%;
  }

  :deep(.van-field__control) {
    display: flex;
    align-items: center;
    min-height: 44px;
  }

  :deep(.van-field__body) {
    display: flex;
    align-items: center;
  }

  // 查询按钮样式
  .search-button {
    min-width: 60px;
    border-radius: 4px;
  }
}

.orders-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .scrollable-orders {
    flex: 1;
    overflow-y: auto;
    margin: 12px 0;

    // 隐藏滚动条但保持滚动功能
    scrollbar-width: none; // Firefox
    -ms-overflow-style: none; // IE/Edge

    &::-webkit-scrollbar {
      display: none; // WebKit browsers
    }

    :deep(.van-cell-group) {
      margin-top: 0;
      margin-bottom: 0;
    }
  }
}

.section-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px 12px;
  font-size: 16px;
  font-weight: bold;
  color: $black;
  padding-bottom: 0;

  .order-count {
    font-size: 12px;
    color: $grey1;
    font-weight: normal;
  }
}

.order-summary {
  margin: 0 16px;
  padding: 12px;
  background: $white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 16px;

  .summary-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;

    .total-amount {
      color: $primary;
      font-weight: bold;
    }
  }
}

.invoice-form-section {
  margin: 16px 0;
}

.submit-section {
  margin: 24px 16px 16px;
}

// 固定在底部的步骤按钮样式
.fixed-step-buttons {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: $white;
  padding: 16px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom));
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.1);
  border-top: 1px solid #ebedf0;
  height: 88px;

  // 单个按钮样式（步骤1）
  &:not(.step-2-buttons) {
    .van-button {
      width: 100%;
      border-radius: 8px;
    }
  }

  // 双按钮样式（步骤2）
  &.step-2-buttons {
    display: flex;
    gap: 12px;

    .prev-button {
      flex: 1;
      color: #fff;
    }

    .submit-button {
      flex: 2;
    }
  }
}

// 选中订单汇总样式 (步骤2)
.selected-orders-summary {
  margin: 16px 0;

  .scrollable-orders {
    max-height: 200px;
    overflow-y: auto;
    margin: 12px 0;

    // 隐藏滚动条但保持滚动功能
    scrollbar-width: none; // Firefox
    -ms-overflow-style: none; // IE/Edge

    &::-webkit-scrollbar {
      display: none; // WebKit browsers
    }

    :deep(.van-cell-group) {
      margin-top: 0;
      margin-bottom: 0;
    }

    // 删除按钮垂直居中
    :deep(.van-cell) {
      align-items: center;
    }
  }

  .total-summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: $white;
    margin: 0 16px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    font-size: 14px;

    .total-amount {
      color: $primary;
      font-weight: bold;
    }
  }
}

// 自定义样式覆盖
:deep(.van-cell-group) {
  margin: 12px 16px;
}

:deep(.van-field__label) {
  color: $black;
  font-weight: 500;
}

:deep(.van-button--primary) {
  background-color: $primary;
  border-color: $primary;
}

// 确保复选框为圆形样式
:deep(.van-checkbox__icon) {
  border-radius: 50% !important;
}

:deep(.van-checkbox__icon--checked) {
  background-color: $primary;
  border-color: $primary;
  border-radius: 50% !important;
}

// 全局隐藏所有滚动条
* {
  scrollbar-width: none; // Firefox
  -ms-overflow-style: none; // IE/Edge

  &::-webkit-scrollbar {
    display: none; // WebKit browsers
  }
}

// 确保Vant组件内的滚动条也被隐藏
:deep(*) {
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;

  &::-webkit-scrollbar {
    display: none !important;
  }
}
</style>
